import React, { ReactNode, useState } from "react";
import { Flex, Grid, Input, Text } from "@chakra-ui/react";
import { Formik, FormikErrors } from "formik";
import CustomApprovalModal from "@/shared/components/custom-modal";
import useAppToasts from "@/features/line-sheet-sets/hooks/use-app-toasts";
import { useQueryClient } from "@tanstack/react-query";
import useGetBuyerRemitterName, {
  buyerRemitterNameQueryKey,
} from "@/shared/hooks/bank-info/useGetBuyerRemitterName";
import useGetBuyerBusinessLicense from "@/shared/hooks/business-license/useGetBuyerBusinessLicense";
import useUpdateBuyerBusinessLicense from "@/shared/hooks/business-license/useUpdateBuyerBusinessLicense";
import useGetBuyer from "@/shared/hooks/buyer/useGetBuyer";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import useApplyPathPickMembership from "@/features/pathpick/features/membership/hooks/mutation/useApplyPathPickMembership";
import useGetPathPickMembershipPlans from "@/features/pathpick/features/membership/hooks/query/useGetPathPickMembershipPlans";
import PathPickMembershipBillingCycleToggle from "@/features/pathpick/features/membership/components/PathPickMembershipBillingCycleToggle";
import MembershipInfoBox from "@/features/pathpick/features/membership/components/MembershipInfoBox";
import PathPickMembershipBusinessInfoSection, {
  MembershipBusinessInfoFormValues,
} from "@/features/pathpick/features/membership/components/plan-change/PathPickMembershipBusinessInfoSection";
import {
  BUSINESS_REGISTRATION_NUMBER_PATTERN,
  formatBusinessRegistrationNumber,
} from "@/shared/constants/business-registration-certificate";
import {
  MEMBERSHIP_BANK_ACCOUNT,
  MEMBERSHIP_BILLING_CYCLE_BODY_VALUE,
  selectMembershipPlanPriceView,
} from "@/features/pathpick/features/membership/constants";
import {
  MembershipBillingCycle,
  MembershipPlan,
} from "@/features/pathpick/features/membership/types";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  // enterprise는 상담하기만 노출되므로 이 모달에 진입하지 않는다
  targetPlan: MembershipPlan;
  // undefined면 현재 플랜을 특정하지 못한 상태 (구 플랜 코드 포함)
  currentPlan?: MembershipPlan;
  billingCycle: MembershipBillingCycle;
  // 페이지가 들고 있는 상태를 그대로 바꾼다 — 모달에서 고른 주기가 뒤 배경 플랜 카드에도 반영된다.
  onChangeBillingCycle: (billingCycle: MembershipBillingCycle) => void;
  // 신규 가입이면 첫 달 50% 할인가로 청구되고 연 할인율 배지도 프로모션 기준이 된다.
  isFirstSubscription: boolean;
  // 접수 성공 — 페이지가 이 모달을 닫고 입금 대기 요약 모달로 넘긴다.
  onApplied: () => void;
}

// 사업자 정보 칸은 별도 컴포넌트가 채우고, 이 폼은 입금자명·세금계산서 이메일을 더한다.
interface PlanChangeFormValues extends MembershipBusinessInfoFormValues {
  depositorName: string;
}

// 라벨 + 필수 표시 — 기본 정보 설정 모달(PathPickBasketBuyerInfoModal)과 같은 관례를 쓴다.
const FieldLabel = ({
  children,
  isRequired,
}: {
  children: ReactNode;
  isRequired?: boolean;
}) => (
  <Flex gap={"2px"} alignItems={"center"}>
    <Text textStyle={"body-sm-bold"} color={"#1D1D1B"}>
      {children}
    </Text>
    {isRequired && (
      <Text textStyle={"body-sm-bold"} color={"#EE7D7A"}>
        {"*"}
      </Text>
    )}
  </Flex>
);

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const inputStyleProps = {
  fontSize: "13px",
  border: "0.5px solid #D4D4D1",
  borderRadius: "8px",
  background: "#FCFCFB",
  _placeholder: { color: "#8F8F8C" },
};

// 저장된 사업자등록번호는 하이픈 유무가 응답마다 다를 수 있어 숫자만 비교한다.
const toDigits = (value: string | null | undefined) =>
  (value ?? "").replace(/[^0-9]/g, "");

function PathPickMembershipPlanChangeModal({
  isOpen,
  onClose,
  targetPlan,
  currentPlan,
  billingCycle,
  onChangeBillingCycle,
  isFirstSubscription,
  onApplied,
}: Props) {
  const { t, tNumberString } = useI18nHelper();
  const { info } = useAppToasts();
  // 입금자명 프리필 — 지난 주문·멤버십 신청에 마지막으로 적은 값. 없으면 빈 칸(회사명을 대신 채우지 않는다).
  const { remitterName: savedRemitterName } = useGetBuyerRemitterName();
  const queryClient = useQueryClient();
  // 등록된 사업자등록증 — 폼 초기값을 채우고, 신청 직전 저장이 필요한지 판단하는 기준이다.
  const { businessLicense } = useGetBuyerBusinessLicense();
  // 세금계산서 발행 이메일은 사업자등록증이 아니라 바이어 정보에 저장돼 있다.
  const { buyer } = useGetBuyer();
  const { mutateAsync: updateBusinessLicense, isPending: isSavingBusinessInfo } =
    useUpdateBuyerBusinessLicense();
  // 신청 바디의 plan_id는 정수인데 화면 플랜 id는 문자열이라, code로 서버 id를 찾아 쓴다.
  const { findPlanIdByCode } = useGetPathPickMembershipPlans();
  const { mutateAsync: applyMembership, isPending: isApplying } =
    useApplyPathPickMembership();

  // 자동 인식이 끝나야 사업자 정보가 확정되므로 그동안은 신청 버튼을 잠근다.
  const [isCertificateParsing, setIsCertificateParsing] = useState(false);

  const isYearly = billingCycle === "yearly";
  const mode = !currentPlan
    ? "new"
    : targetPlan.order > currentPlan.order
    ? "upgrade"
    : "downgrade";
  // 미가입·만료에서 처음 시작하는 경우는 "변경"이 아니라 "시작"이다.
  // 플랜 카드가 업/다운그레이드를 채널톡으로 보내므로 실제로 이 모달이 열리는 건 이 경우뿐이다.
  const isNewSubscription = mode === "new";
  // 카드와 같은 selector를 써서 모달 청구액이 카드에 보여준 금액과 어긋나지 않게 한다.
  const price = selectMembershipPlanPriceView(
    targetPlan,
    billingCycle,
    isFirstSubscription
  );

  const applyNoteKey =
    mode === "upgrade"
      ? "membership:change_modal.apply_note_upgrade"
      : mode === "downgrade"
      ? "membership:change_modal.apply_note_downgrade"
      : "membership:change_modal.apply_note_new";

  // 접수 안내는 적용 시점이 달라 업/다운그레이드/신규를 나눈다.
  const submitToastKey =
    mode === "upgrade"
      ? "membership:change_modal.submit_toast_upgrade"
      : mode === "downgrade"
      ? "membership:change_modal.submit_toast_downgrade"
      : "membership:change_modal.submit_toast_new";

  // 플랜 목록을 아직 못 읽었거나 code가 맞는 플랜이 없으면 신청 자체가 불가능하다.
  const planId = findPlanIdByCode(targetPlan.englishName);

  // 세금계산서 이메일은 항상 필수다. 사업자등록번호·사업자명은 사업자등록증을 등록한 뒤에만
  // 입력할 수 있어, 파일이 붙은 경우에만 검증한다.
  const validate = (values: PlanChangeFormValues) => {
    const errors: FormikErrors<PlanChangeFormValues> = {};
    // 입금자명이 없으면 어드민이 입금 건을 신청과 맞출 수 없다.
    if (!values.depositorName.trim()) {
      errors.depositorName = t("membership:change_modal.depositor_error");
    }
    if (values.certificateFileId !== null) {
      if (
        !BUSINESS_REGISTRATION_NUMBER_PATTERN.test(
          values.businessRegistrationNumber.trim()
        )
      ) {
        errors.businessRegistrationNumber =
          "사업자등록번호 형식이 올바르지 않습니다. (예: 123-45-67890)";
      }
      if (!values.businessName.trim()) {
        errors.businessName = "사업자명을 입력해 주세요.";
      }
    }
    if (!EMAIL_REGEX.test(values.taxEmail.trim())) {
      errors.taxEmail = t("membership:change_modal.tax_email_error");
    }
    return errors;
  };

  // 파일을 새로 붙였거나 사업자 정보를 고친 경우에만 저장 API를 한 번 더 태운다.
  // 세금계산서 이메일은 신청 바디로 함께 보내면 서버가 저장하므로 여기서 따지지 않는다.
  const hasBusinessInfoChanged = (values: PlanChangeFormValues) =>
    values.certificateFileId !== (businessLicense?.id ?? null) ||
    toDigits(values.businessRegistrationNumber) !==
      toDigits(businessLicense?.businessRegistrationNumber) ||
    values.businessName.trim() !== (businessLicense?.businessName ?? "");

  const handleSubmit = async (values: PlanChangeFormValues) => {
    if (planId === undefined || values.certificateFileId === null) {
      return;
    }
    try {
      // 신청 바디에는 사업자등록번호·사업자명 필드가 없어, 먼저 사업자 정보로 저장한다.
      // 저장이 실패하면 신청까지 진행하지 않는다 (토스트는 저장 훅이 띄운다).
      if (hasBusinessInfoChanged(values)) {
        await updateBusinessLicense({
          businessRegistrationCertificateFileId: values.certificateFileId,
          businessRegistrationNumber:
            values.businessRegistrationNumber.trim() || null,
          businessName: values.businessName.trim() || null,
        });
      }
      await applyMembership({
        planId,
        billingCycle: MEMBERSHIP_BILLING_CYCLE_BODY_VALUE[billingCycle],
        remitterName: values.depositorName.trim() || null,
        // 세금계산서는 항상 발행 요청으로 보내며, 수신 이메일은 폼에서 필수로 받는다.
        taxInvoiceRequested: true,
        taxInvoiceEmail: values.taxEmail.trim(),
        // 방금 등록한 파일이든 기존 파일이든 신청에 쓸 파일 id를 명시한다.
        businessRegistrationCertificateFileId: values.certificateFileId,
      });
      info(t(submitToastKey, { plan: targetPlan.englishName }));
      // 서버가 이번 입금자명을 기본값으로 기억했다 — 다음 신청·주문 때 새 값이 채워지게 한다.
      queryClient.invalidateQueries({ queryKey: [buyerRemitterNameQueryKey] });
      // 닫고 끝내면 입금 계좌를 다시 볼 방법이 없다 — 접수 내용 요약 모달로 넘긴다.
      onApplied();
    } catch {
      // 실패 토스트는 각 mutation의 onError가 띄운다 — 모달은 열어 둔다.
    }
  };

  return (
    <Formik<PlanChangeFormValues>
      enableReinitialize
      initialValues={{
        depositorName: savedRemitterName ?? "",
        // 등록된 사업자등록증이 있으면 그 파일 id로 시작한다 (없으면 모달에서 등록해야 한다).
        certificateFileId: businessLicense?.id ?? null,
        businessRegistrationNumber: formatBusinessRegistrationNumber(
          businessLicense?.businessRegistrationNumber ?? ""
        ),
        businessName: businessLicense?.businessName ?? "",
        // 회원정보에 저장해 둔 세금계산서 발행 이메일을 기본값으로 쓴다.
        taxEmail: buyer?.taxInvoiceEmail ?? "",
      }}
      validate={validate}
      onSubmit={handleSubmit}
    >
      {({ values, errors, handleChange, handleSubmit, isValid }) => (
        <CustomApprovalModal
          isOpen={isOpen}
          onClose={onClose}
          // minWidth가 maxWidth를 이기고, 가운데 정렬된 모달은 왼쪽으로 넘치면
          // 스크롤로도 닿을 수 없어 화면 폭으로 클램프한다.
          minWidth={"min(640px, 90dvw)"}
          scrollBehavior={"inside"}
          isCentered={true}
        >
          <CustomApprovalModal.Header
            onClose={onClose}
            title={t(
              isNewSubscription
                ? "membership:change_modal.title_new"
                : "membership:change_modal.title",
              { plan: targetPlan.englishName }
            )}
          />
          <CustomApprovalModal.Body>
            <Flex flexDirection={"column"} gap={"12px"}>
              {/* 결제 주기 — 페이지 상태를 직접 바꿔 뒤 배경 플랜 카드와 함께 움직인다 */}
              <Flex justifyContent={"center"}>
                <PathPickMembershipBillingCycleToggle
                  value={billingCycle}
                  isFirstSubscription={isFirstSubscription}
                  onChange={onChangeBillingCycle}
                />
              </Flex>

              {/* 변경 후 결제 금액 요약 */}
              <Flex
                flexDirection={"column"}
                gap={"4px"}
                padding={"16px"}
                border={"0.5px solid #A8CEFA"}
                borderRadius={"8px"}
                background={"#F0F7FF"}
              >
                <Flex justifyContent={"space-between"} alignItems={"center"}>
                  <Text textStyle={"caption-medium"} color={"#8F8F8C"}>
                    {t("membership:change_modal.amount_label")}
                  </Text>
                  {price?.yearlyDiscountRate != null && (
                    <Flex
                      padding={"1px 8px"}
                      alignItems={"center"}
                      borderRadius={"6px"}
                      border={"0.5px solid #A8CEFA"}
                      background={"#FFFFFF"}
                    >
                      <Text textStyle={"caption-bold"} color={"#1272EF"}>
                        {t("membership:change_modal.discount_badge", {
                          rate: price.yearlyDiscountRate,
                        })}
                      </Text>
                    </Flex>
                  )}
                </Flex>
                <Text textStyle={"body-sm-bold"} color={"#1272EF"}>
                  {t(
                    isYearly
                      ? "membership:billing.yearly"
                      : "membership:billing.monthly"
                  )}
                </Text>
                <Text
                  textStyle={"body-lg-bold"}
                  fontSize={"22px"}
                  color={"#1D1D1B"}
                >
                  {price !== null &&
                    t("membership:price.won", {
                      amount: tNumberString(price.chargedPrice),
                    })}
                </Text>
                <Text textStyle={"caption-medium"} color={"#8F8F8C"}>
                  {t("membership:price.vat_excluded")}
                </Text>
                <Text textStyle={"body-sm-medium"} color={"#444440"}>
                  {t(applyNoteKey)}
                </Text>
              </Flex>

              {/* 입금 계좌 안내 */}
              {/* 좁은 폭에서 3열이면 칸당 ~105px이라 계좌번호가 깨진다 */}
              <Grid
                templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }}
                gap={"12px"}
              >
                <MembershipInfoBox
                  label={t("membership:change_modal.bank")}
                  value={MEMBERSHIP_BANK_ACCOUNT.bankName}
                />
                <MembershipInfoBox
                  label={t("membership:change_modal.account_number")}
                  value={MEMBERSHIP_BANK_ACCOUNT.accountNumber}
                />
                <MembershipInfoBox
                  label={t("membership:change_modal.account_holder")}
                  value={MEMBERSHIP_BANK_ACCOUNT.accountHolder}
                />
              </Grid>

              {/* 입금자명 */}
              <Flex flexDirection={"column"} gap={"6px"}>
                <FieldLabel isRequired>
                  {t("membership:change_modal.depositor_label")}
                </FieldLabel>
                <Input
                  name={"depositorName"}
                  value={values.depositorName}
                  onChange={handleChange}
                  {...inputStyleProps}
                  {...(errors.depositorName && {
                    border: "1px solid #EE7D7A",
                    _focusVisible: { borderColor: "#EE7D7A" },
                  })}
                />
                {errors.depositorName && (
                  <Text textStyle={"caption-medium"} color={"#EE7D7A"}>
                    {errors.depositorName}
                  </Text>
                )}
              </Flex>

              {/* 등록된 사업자 정보 — 회원정보 수정의 사업자 정보란과 같은 구성으로 여기서 등록한다 */}
              <Flex flexDirection={"column"} gap={"6px"}>
                <FieldLabel>
                  {t("membership:change_modal.business_info_title")}
                </FieldLabel>
                <PathPickMembershipBusinessInfoSection
                  onParsingChange={setIsCertificateParsing}
                />
              </Flex>

              {/* 세금계산서 — 발행 이메일은 항상 필수로 받는다 */}
              <Flex
                flexDirection={"column"}
                gap={"8px"}
                padding={"12px"}
                border={"0.5px solid #D4D4D1"}
                borderRadius={"8px"}
              >
                <Text textStyle={"body-md-bold"} color={"#1D1D1B"}>
                  {t("membership:change_modal.tax_invoice_section_title")}
                </Text>
                <Flex flexDirection={"column"} gap={"6px"}>
                  <FieldLabel isRequired>
                    {t("membership:change_modal.tax_email_label")}
                  </FieldLabel>
                  <Input
                    name={"taxEmail"}
                    value={values.taxEmail}
                    onChange={handleChange}
                    placeholder={t(
                      "membership:change_modal.tax_email_placeholder"
                    )}
                    {...inputStyleProps}
                    {...(errors.taxEmail && {
                      border: "1px solid #EE7D7A",
                      _focusVisible: { borderColor: "#EE7D7A" },
                    })}
                  />
                  {errors.taxEmail && (
                    <Text textStyle={"caption-medium"} color={"#EE7D7A"}>
                      {errors.taxEmail}
                    </Text>
                  )}
                </Flex>
              </Flex>

              {/* 변경 내용 */}
              <Flex
                flexDirection={"column"}
                gap={"4px"}
                padding={"12px"}
                border={"0.5px solid #D4D4D1"}
                borderRadius={"8px"}
              >
                <Text textStyle={"body-md-bold"} color={"#1D1D1B"}>
                  {t("membership:change_modal.summary_title")}
                </Text>
                <Text textStyle={"body-sm-medium"} color={"#444440"}>
                  {currentPlan
                    ? t("membership:change_modal.summary_change", {
                        current: currentPlan.englishName,
                        target: targetPlan.englishName,
                      })
                    : t("membership:change_modal.summary_new", {
                        target: targetPlan.englishName,
                      })}
                </Text>
              </Flex>
            </Flex>
          </CustomApprovalModal.Body>
          <CustomApprovalModal.Footer
            cancelName={t("membership:change_modal.close")}
            confirmName={t(
              isNewSubscription
                ? "membership:change_modal.submit_new"
                : "membership:change_modal.submit"
            )}
            onCancel={onClose}
            onConfirm={() => handleSubmit()}
            isLoading={isSavingBusinessInfo || isApplying}
            // 사업자등록증이 붙어 있어야 신청할 수 있다 — 없으면 위 섹션에서 바로 등록한다.
            isConfirmDisabled={
              !isValid ||
              planId === undefined ||
              values.certificateFileId === null ||
              isCertificateParsing
            }
            confirmBtnProps={{ width: "120px" }}
          />
        </CustomApprovalModal>
      )}
    </Formik>
  );
}

export default PathPickMembershipPlanChangeModal;
